import { numberMask } from '../../lib/numberMask'
import type { NumberScale } from '../../lib/numberParse'
import { MaskedField } from './MaskedField'

type NumberFieldProps = {
  value: number | null
  /** Fires on every keystroke, not only on blur. */
  onValueChange: (next: number | null) => void
  /** BCP-47 tag. Supplied by the caller — this package has no locale of its own. */
  locale: string
  format?: 'decimal' | 'currency'
  /** Only meaningful when `format` is `'currency'`. */
  currency?: string
  minFractionDigits?: number
  maxFractionDigits?: number
  allowNegative?: boolean
  /** Off for years, counts and multipliers, where a thousands separator is wrong. */
  grouping?: boolean
  /**
   * Denomination of the stored value, written into the mask as the locale's compact abbreviation —
   * `$1M` in en-CA, `1 M$` in fr-CA for `'million'` — without scaling the number: the field reads
   * and writes `1` to mean one million.
   */
  scale?: NumberScale
  id?: string
  disabled?: boolean
  placeholder?: string
  className?: string
  onBlur?: () => void
  'aria-label'?: string
  'aria-invalid'?: boolean
}

/**
 * A numeric {@link MaskedField} — the common case, spelled without the mask.
 *
 * Reach for `MaskedField` with a mask of your own when the value is not a number, or not formatted
 * the way `Intl` formats one.
 */
function NumberField({
  locale,
  format,
  currency,
  minFractionDigits,
  maxFractionDigits,
  allowNegative,
  grouping,
  scale,
  ...rest
}: NumberFieldProps) {
  return (
    <MaskedField
      mask={numberMask({
        locale,
        format,
        currency,
        minFractionDigits,
        maxFractionDigits,
        allowNegative,
        grouping,
        scale,
      })}
      {...rest}
    />
  )
}

export { NumberField }
